import type { InventoryItem } from '../../redux/inventorySlice';
import type { DetectedFoodItem } from '../../types/foodRecognition';
import type { PantryLocation } from '../../utils/pantryLocation';
import { pantryDisplayName, pantryUnitName } from '../../utils/pantryDisplayName';

/**
 * Reviewing a delivery that is already on the shelf.
 *
 * A forwarded Lufa order is written to the pantry by the server, so unlike a
 * receipt scan there is nothing left to add. The review is the receipt scan's
 * screen anyway (`CaptureReview`) — one row per food, a zone chip to correct
 * where it lives, a checkbox for the line that never arrived — and confirming it
 * turns those edits into removals and moves of rows that already exist.
 */

/** The delivery's rows, in the order the order email listed them. */
export function deliveryRows(items: InventoryItem[], importId: string): InventoryItem[] {
  const position = (item: InventoryItem) => {
    const suffix = item.id.slice(importId.length + 1);
    return /^\d+$/.test(suffix) ? Number(suffix) : Number.MAX_SAFE_INTEGER;
  };
  return items
    .filter(item => item?.importId === importId)
    .sort((a, b) => position(a) - position(b));
}

/** The rows as the capture review reads them. Same order, one for one. */
export function toDetectedItems(rows: InventoryItem[], language: string): DetectedFoodItem[] {
  return rows.map(row => ({
    name: pantryDisplayName(row, language),
    category: row.category as DetectedFoodItem['category'],
    confidence: row.confidence ?? 90,
    quantity_estimate: row.quantity ?? 'unknown',
    visible_condition: 'unknown',
    ...(row.quantity !== undefined ? { quantity: row.quantity } : {}),
    ...(row.unit ? { unit: pantryUnitName(row.unit, language, row.quantity) } : {}),
    // The zone the server filed it in. Passed through so the chip shows where
    // the food actually is, not a fresh guess the shelf never received.
    ...(row.location ? { location: row.location } : {}),
  }));
}

/** A row of the review as the cook left it. */
export interface ReviewedRow {
  isAccepted: boolean;
  location?: PantryLocation;
  locationOverridden?: boolean;
}

export interface DeliveryReviewPlan {
  /** Unchecked: the line never arrived, or is not food the cook keeps. */
  remove: InventoryItem[];
  /** Kept, but in another zone than the one the server picked. */
  moves: Array<{ item: InventoryItem; zone: PantryLocation; learned: boolean }>;
  kept: number;
}

/**
 * What confirming the review does to the pantry.
 *
 * `reviewed` comes back from `CaptureReview` in the order it was given, which
 * is the order of `rows` — the capture review never reorders its list.
 */
export function planDeliveryReview(rows: InventoryItem[], reviewed: ReviewedRow[]): DeliveryReviewPlan {
  if (rows.length !== reviewed.length) {
    throw new Error(`Delivery review out of step: ${rows.length} rows, ${reviewed.length} reviewed`);
  }

  const plan: DeliveryReviewPlan = { remove: [], moves: [], kept: 0 };
  rows.forEach((item, i) => {
    const row = reviewed[i];
    if (!row.isAccepted) {
      plan.remove.push(item);
      return;
    }
    plan.kept++;
    if (row.location && row.location !== item.location) {
      // Only a zone the cook picked is a fact about their kitchen. A row with
      // no stored zone gets the review's own guess written, never learned.
      plan.moves.push({ item, zone: row.location, learned: !!row.locationOverridden });
    }
  });
  return plan;
}
